import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

/**
 * HowItWorks — upgraded Landing Page "How it works" showcase.
 *
 * Three larger, accent-differentiated workflow cards, each carrying a
 * code-rendered mini preview of the actual Stockaily product moment
 * (Search → Valuation → Thesis). Copy is fully unchanged from the
 * approved v1; this pass only upgrades the visual treatment.
 *
 *   Card 1 (Search)     — Stockaily blue        #3182CE
 *   Card 2 (Valuation)  — Emerald/teal          #3FAE89
 *   Card 3 (Thesis)     — Amber-gold + violet   #D9A441 / #9B7BFF
 *
 * Depth is created via:
 *   • per-card thin colored border + soft outer glow tuned to the
 *     card's accent (never rainbow, never saturated fills)
 *   • one localised atmospheric gradient behind the whole section
 *   • a subtle 3-stop gradient line connecting the cards on desktop
 *
 * Motion:
 *   • Framer-motion staggered entrance (0/0.08/0.16 s delay).
 *   • `useReducedMotion` disables all entrance animation and any
 *     smooth-scroll on the CTA. No continuous animations, no
 *     auto-cycling states, no pulsing / floating.
 *
 * All product previews are static schematic renders of the real
 * Stockaily UI — no live API calls, no fabricated performance
 * values. Where real numbers appear (PYPL 73 Opportunity Score) they
 * are the same values already publicly rendered on the current
 * Landing Page's AI Market Snapshot.
 */
export default function HowItWorks() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const sectionRef = React.useRef(null);
  // Track whether the section is on-screen AND the document isn't
  // hidden so we can pause the Card-3 shine loop while offscreen and
  // resume cleanly when it comes back — avoids invisible animations
  // consuming compositor time and drift across the four staggered
  // quadrants.  Also disabled outright under prefers-reduced-motion.
  const [shinePaused, setShinePaused] = React.useState(true);
  React.useEffect(() => {
    if (reduceMotion) {
      setShinePaused(true);
      return;
    }
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let inView = false;
    const compute = () => setShinePaused(!(inView && !document.hidden));
    const obs = new IntersectionObserver(
      (entries) => {
        inView = entries.some((e) => e.isIntersecting && e.intersectionRatio > 0.2);
        compute();
      },
      { threshold: [0, 0.2, 0.5] }
    );
    obs.observe(el);
    const onVis = () => compute();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      obs.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reduceMotion]);

  const steps = [
    {
      key: "find",
      num: "01",
      accent: "#3182CE",
      accentRgb: "49,130,206",
      Preview: SearchPreview,
    },
    {
      key: "value",
      num: "02",
      accent: "#3FAE89",
      accentRgb: "63,174,137",
      Preview: ValuationPreview,
    },
    {
      key: "conviction",
      num: "03",
      accent: "#D9A441",
      accentRgb: "217,164,65",
      Preview: ThesisPreview,
    },
  ];

  const handleCtaClick = () => {
    const input = document.querySelector('[data-testid="hero-search-input"]');
    if (!input) return;
    input.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "center",
    });
    window.setTimeout(
      () => {
        try {
          input.focus({ preventScroll: true });
        } catch {
          input.focus();
        }
      },
      reduceMotion ? 0 : 250
    );
  };

  return (
    <section
      ref={sectionRef}
      data-testid="landing-how-it-works"
      data-shine-paused={shinePaused ? "true" : "false"}
      aria-labelledby="how-it-works-heading"
      className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-24 isolate"
    >
      {/* Section atmosphere removed entirely — all colored treatment
          on the three StepCards is now confined to their controlled
          per-card external glow (see StepCard below).  There is no
          shared radial-gradient atmosphere layer behind the cards,
          so no green/blue/amber wash can reach the section heading
          or bleed across / behind the cards. */}

      {/* Section header */}
      <div className="text-center max-w-2xl mx-auto">
        <span
          data-testid="how-it-works-eyebrow"
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80 font-medium"
        >
          {t("landing.how_it_works_eyebrow")}
        </span>
        <h2
          id="how-it-works-heading"
          data-testid="how-it-works-title"
          className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground"
        >
          {t("landing.how_it_works_title")}
        </h2>
        <p
          data-testid="how-it-works-subtitle"
          className="mt-2.5 text-sm sm:text-base text-muted-foreground/90 leading-relaxed"
        >
          {t("landing.how_it_works_subtitle")}
        </p>
      </div>

      {/* Workflow cards */}
      <div className="mt-10 sm:mt-12 relative">
        {/* Desktop connector — thin 3-stop gradient behind the cards */}
        <div
          aria-hidden="true"
          data-testid="how-it-works-connector"
          className="hidden md:block absolute top-[88px] left-[12%] right-[12%] h-px pointer-events-none z-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(49,130,206,0) 0%, rgba(49,130,206,0.55) 16%, rgba(63,174,137,0.55) 50%, rgba(217,164,65,0.55) 84%, rgba(217,164,65,0) 100%)",
          }}
        />
        <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 items-stretch">
          {steps.map((s, idx) => (
            <StepCard
              key={s.key}
              stepKey={s.key}
              testId={`how-it-works-step-${s.key}`}
              num={s.num}
              category={t(`landing.how_it_works_step_${s.key}_cat`)}
              title={t(`landing.how_it_works_step_${s.key}_title`)}
              description={t(`landing.how_it_works_step_${s.key}_desc`)}
              accent={s.accent}
              accentRgb={s.accentRgb}
              Preview={s.Preview}
              index={idx}
              reduceMotion={reduceMotion}
            />
          ))}
        </ol>
      </div>

      {/* CTA — understated, reuses the existing Hero search flow */}
      <div className="mt-10 sm:mt-12 flex justify-center">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={handleCtaClick}
          data-testid="how-it-works-cta"
          className="group rounded-full px-6 h-10 gap-2 text-sm border-border/70 bg-card/40 hover:bg-card/70 hover:border-[#3182CE]/40 transition-colors"
        >
          {t("landing.how_it_works_cta")}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
}

function StepCard({
  stepKey,
  testId,
  num,
  category,
  title,
  description,
  accent,
  accentRgb,
  Preview,
  index,
  reduceMotion,
}) {
  const { t } = useTranslation();
  const initial = reduceMotion ? false : { opacity: 0, y: 12 };
  const whileInView = reduceMotion ? undefined : { opacity: 1, y: 0 };
  // Per-card external glow only.  All three StepCards now use an
  // identical fully opaque neutral-dark interior — no translucent
  // bg-card/50, no backdrop-blur, no colored gradients, no colored
  // inset rings, no decorative pseudo-elements — so the only colored
  // treatment on the outer card apart from its border is a single
  // restrained downward-biased accent glow.  Alphas tuned so the
  // three colors read at approximately equal perceived intensity
  // (blue slightly stronger, amber slightly weaker to compensate
  // for its natural brightness on dark backgrounds).
  const glowAlpha = stepKey === "find" ? 0.23 : stepKey === "value" ? 0.22 : 0.2;
  const cardClassName =
    "relative rounded-2xl p-5 sm:p-6 flex flex-col min-h-[360px] sm:min-h-[380px]";
  const cardStyle = {
    background: "#0A0A0C",
    border: `1px solid rgba(${accentRgb},0.32)`,
    boxShadow:
      // Restrained downward-biased accent glow (only external
      // colored treatment on the card).
      `0 18px 38px -24px rgba(${accentRgb},${glowAlpha}),` +
      // Neutral black depth shadow.
      ` 0 20px 40px -24px rgba(0,0,0,0.55),` +
      // Subtle inner highlight for depth (neutral white, not tinted).
      ` 0 1px 0 0 rgba(255,255,255,0.03) inset`,
  };
  return (
    <motion.li
      data-testid={testId}
      data-step-key={stepKey}
      initial={initial}
      whileInView={whileInView}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
      className={cardClassName}
      style={cardStyle}
    >
      {/* Header — step number + subtle category label */}
      <div className="flex items-center gap-2.5">
        <span
          data-testid={`${testId}-num`}
          className="font-mono text-[11px] tracking-[0.22em]"
          style={{ color: accent }}
        >
          {num}
        </span>
        <span aria-hidden="true" className="w-px h-3 bg-border/60" />
        <span
          data-testid={`${testId}-category`}
          className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/75 font-medium"
        >
          {category}
        </span>
      </div>

      {/* Product visual.
          Note: the wrapper is aria-hidden for the illustrations,
          BUT Step 2 exposes a concise accessible description of
          the mini "NOW vs FAIR VALUE" comparison graphic via a
          visually-hidden sibling below so screen readers can still
          understand the illustration's meaning. */}
      <div
        data-testid={`${testId}-preview`}
        className="mt-4 mb-5 flex-1 min-h-[168px] flex items-stretch"
        aria-hidden="true"
      >
        <Preview accent={accent} accentRgb={accentRgb} />
      </div>
      {stepKey === "value" ? (
        <span
          data-testid="hiw-valuation-graph-sr-description"
          className="sr-only"
        >
          {t("landing.hiw_step2_graph_aria")}
        </span>
      ) : null}

      {/* Approved title + description — unchanged from v1 */}
      <h3
        data-testid={`${testId}-title`}
        className="text-[16px] sm:text-[17px] font-semibold text-foreground tracking-tight"
      >
        {title}
      </h3>
      <p
        data-testid={`${testId}-desc`}
        className="mt-2 text-[13px] sm:text-[13.5px] text-muted-foreground/85 leading-relaxed"
      >
        {description}
      </p>
    </motion.li>
  );
}

/* ------------------------------------------------------------------ */
/* Product-preview panels — all static, all schematic, no live data.   */
/* ------------------------------------------------------------------ */

function SearchPreview({ accent, accentRgb }) {
  // Sample rows use ONLY tickers already appearing publicly on the
  // Landing Page (AAPL, NVDA, GOOGL are the hero placeholder examples;
  // "Select a company to analyse" is the intended visual message).
  const rows = [
    { t: "AAPL", n: "Apple Inc.", ex: "NASDAQ" },
    { t: "NVDA", n: "NVIDIA Corp.", ex: "NASDAQ" },
    { t: "GOOGL", n: "Alphabet Inc.", ex: "NASDAQ" },
  ];

  // Micro-animation: caret → type "AAPL" → highlight matching row → long calm hold → clean reset → repeat.
  // Cycles roughly every 12.5 seconds while the section is visible.
  // Purely presentational; disabled under prefers-reduced-motion.
  const prefersReduced = useReducedMotion();
  const [typed, setTyped] = React.useState(prefersReduced ? "AAPL" : "");
  const [highlight, setHighlight] = React.useState(prefersReduced);
  const containerRef = React.useRef(null);
  const timersRef = React.useRef([]);
  const stateRef = React.useRef({ visible: false, running: false });
  React.useEffect(() => {
    if (prefersReduced) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const TARGET = "AAPL";

    const clearAllTimers = () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };
    const schedule = (fn, delay) => {
      const id = setTimeout(fn, delay);
      timersRef.current.push(id);
      return id;
    };
    // One full cycle: clear → caret pause → type → highlight → long hold → repeat.
    const runCycle = () => {
      if (!stateRef.current.visible) {
        stateRef.current.running = false;
        return;
      }
      stateRef.current.running = true;
      // Fresh state
      clearAllTimers();
      setTyped("");
      setHighlight(false);
      // Brief pause on empty state with caret before typing starts.
      const startDelay = 700;
      TARGET.split("").forEach((_, i) => {
        schedule(() => setTyped(TARGET.slice(0, i + 1)), startDelay + i * 200);
      });
      // Highlight matched row shortly after typing completes.
      const typingEnd = startDelay + TARGET.length * 200; // 1500ms
      schedule(() => setHighlight(true), typingEnd + 400);
      // Long completed-state hold, then quiet clear and next cycle.
      const holdEnd = typingEnd + 400 + 10500; // ~12400ms total per cycle
      schedule(() => {
        setHighlight(false);
        setTyped("");
        // Small breath before next cycle begins so caret briefly blinks alone.
        schedule(() => runCycle(), 300);
      }, holdEnd);
    };

    const stop = () => {
      stateRef.current.running = false;
      clearAllTimers();
    };
    const start = () => {
      if (stateRef.current.running) return;
      runCycle();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else if (stateRef.current.visible) {
        start();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const obs = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some(
          (e) => e.isIntersecting && e.intersectionRatio > 0.3
        );
        stateRef.current.visible = isVisible;
        if (isVisible && !document.hidden) {
          start();
        } else {
          stop();
        }
      },
      { threshold: [0, 0.3, 0.6] }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stop();
    };
  }, [prefersReduced]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded-xl border border-white/10 p-3 sm:p-3.5 flex flex-col gap-2.5"
      style={{ background: "#0A0A0C" }}
      data-testid="hiw-search-preview"
    >
      {/* Search field mock — opaque neutral surface, blue border only. */}
      <div
        className="flex items-center gap-2 rounded-lg border px-3 py-2"
        style={{
          borderColor: `rgba(${accentRgb},0.30)`,
          background: "#0A0A0C",
        }}
      >
        <Search className="w-3.5 h-3.5" style={{ color: accent }} />
        <span
          className="text-[11.5px] font-mono text-foreground/90 min-w-[3ch]"
          data-testid="hiw-search-typed"
          aria-live="polite"
        >
          {typed}
          {!prefersReduced && typed.length < 4 ? <span className="hiw-caret" style={{ color: accent }} aria-hidden="true" /> : null}
        </span>
        <span className="ml-auto flex items-center gap-1 text-[9px] text-muted-foreground/60 font-medium">
          <span className="px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.04]">↵</span>
        </span>
      </div>
      {/* Result rows */}
      <div className="space-y-1">
        {rows.map((r, i) => {
          const isMatch = i === 0 && highlight;
          return (
          <div
            key={r.t}
            data-testid={`hiw-search-row-${r.t}`}
            data-selected={isMatch ? "true" : "false"}
            className="flex items-center justify-between px-2 py-1.5 rounded-md transition-colors"
            style={isMatch ? { background: `rgba(${accentRgb},0.14)`, boxShadow: `inset 0 0 0 1px rgba(${accentRgb},0.35)` } : undefined}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="text-[10.5px] font-mono font-medium shrink-0"
                style={{ color: isMatch ? accent : "rgba(255,255,255,0.75)" }}
              >
                {r.t}
              </span>
              <span className="text-[11px] text-muted-foreground/80 truncate">
                {r.n}
              </span>
            </div>
            <span className="text-[9px] text-muted-foreground/55 font-mono shrink-0 ml-2">
              {r.ex}
            </span>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function ValuationPreview({ accent, accentRgb }) {
  // Landing/Analysis Polish 2 — curated PayPal example inspired by
  // the real Analyze ScorePanel + Price-vs-Fair-Value chart. The
  // numbers are a fixed illustration — the subtle "Example analysis"
  // marker makes this explicit, and no live API is called.  The
  // hierarchy is optimised for a small landing-page card:
  //     PYPL · PayPal    ─────────    Example analysis
  //           77 / 100      AI Opportunity Score
  //         Undervalued by 29.8%
  //     Now $55.03            Fair Value $71.42
  //     ┌── mini "NOW vs FAIR VALUE" comparison graphic ──┐
  return <ValuationExamplePreview accent={accent} accentRgb={accentRgb} />;
}

function ValuationExamplePreview({ accent, accentRgb }) {
  const { t } = useTranslation();
  return (
    <div
      className="w-full rounded-xl border border-white/10 bg-black/40 flex flex-col overflow-hidden"
      style={{ boxShadow: `inset 0 0 24px rgba(${accentRgb},0.06)` }}
      data-testid="hiw-valuation-preview"
    >
      {/* Upper block — condensed real ScorePanel composition.
          Reproduces the layout of the attached PayPal ScorePanel
          crop: prominent score on the left, undervaluation result +
          semantic chips + current price/fair value boxes + a
          compact status row on the right. */}
      <ValuationScorePanelPreview accent={accent} accentRgb={accentRgb} t={t} />
      {/* Realistic compact "Price vs Fair Value" graphic */}
      <ValuationCompactChart accent={accent} accentRgb={accentRgb} t={t} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ValuationScorePanelPreview — condensed real-ScorePanel composition.
// Mirrors the layout of the attached PayPal ScorePanel crop but keeps
// the outer footprint compatible with the small landing-page card.
// ---------------------------------------------------------------------------
function ValuationScorePanelPreview({ accent, accentRgb, t }) {
  return (
    <div
      className="relative px-3 pt-2.5 pb-3 border-b border-white/[0.06]"
      style={{
        background: `linear-gradient(180deg, rgba(${accentRgb},0.10) 0%, rgba(${accentRgb},0.03) 60%, rgba(0,0,0,0) 100%)`,
      }}
    >
      {/* Subtle top row — PYPL · PayPal · [Example marker] */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 leading-none">
          <span
            className="text-[10.5px] font-mono font-medium"
            style={{ color: accent }}
            data-testid="hiw-valuation-ticker"
          >
            PYPL
          </span>
          <span className="text-[9.5px] text-muted-foreground/70 ml-1.5">
            PayPal
          </span>
        </div>
        <span
          data-testid="hiw-valuation-example-marker"
          className="shrink-0 text-[9px] font-medium tracking-[0.04em] text-muted-foreground/55 italic mt-[1px]"
        >
          {t("landing.hiw_step2_example_marker")}
        </span>
      </div>

      {/* Two-column body — score / signal on the left (≈34%),
          assessment + chips + boxes on the right.  The grid ratio
          gives the AI Opportunity Score label enough room to render
          in no more than two lines at desktop width while keeping
          the right column comfortable for chips, price boxes and
          the compact status row. */}
      <div className="grid grid-cols-[34%_1fr] gap-2 items-start">
        {/* Left — AI Opportunity Score + Signal chip */}
        <div className="flex flex-col min-w-0">
          <div
            className="text-[9px] leading-[1.2] uppercase tracking-[0.10em] text-muted-foreground/70 font-medium"
            data-testid="hiw-valuation-score-label"
          >
            {t("landing.hiw_step2_score_label")}
          </div>
          <div
            className="mt-1 font-semibold leading-none tabular flex items-baseline"
            data-testid="hiw-valuation-score"
          >
            <span className="text-[34px]" style={{ color: accent }}>77</span>
            <span className="text-[11px] text-muted-foreground/65 ml-0.5">/100</span>
          </div>
          <span
            data-testid="hiw-valuation-signal-chip"
            className="mt-2 inline-flex items-center gap-1 self-start rounded-full px-1.5 py-[2px] text-[8.5px] font-medium leading-none whitespace-nowrap"
            style={{
              color: accent,
              border: `1px solid rgba(${accentRgb},0.35)`,
              background: `rgba(${accentRgb},0.08)`,
            }}
          >
            <Sparkles className="w-2 h-2 shrink-0" aria-hidden="true" />
            <span className="whitespace-nowrap">{t("landing.hiw_step2_signal_label")}</span>
          </span>
        </div>

        {/* Right — assessment / chips / boxes / status row */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-baseline gap-0.5">
            <TrendUpArrow accent={accent} />
            <span
              data-testid="hiw-valuation-assessment"
              className="text-[15px] font-semibold leading-none tracking-tight truncate"
              style={{ color: accent }}
            >
              {t("landing.hiw_step2_assessment")}
            </span>
          </div>
          {/* Semantic chips */}
          <div className="flex items-center gap-1 flex-wrap">
            <span
              data-testid="hiw-valuation-chip-valuation"
              className="inline-flex items-center rounded-full px-1.5 py-[1px] text-[8.5px] font-medium leading-tight whitespace-nowrap"
              style={{
                color: accent,
                border: `1px solid rgba(${accentRgb},0.32)`,
                background: `rgba(${accentRgb},0.06)`,
              }}
            >
              {t("landing.hiw_step2_chip_valuation")}
            </span>
            <span
              data-testid="hiw-valuation-chip-risk"
              className="inline-flex items-center rounded-full px-1.5 py-[1px] text-[8.5px] font-medium leading-tight whitespace-nowrap"
              style={{
                color: accent,
                border: `1px solid rgba(${accentRgb},0.32)`,
                background: `rgba(${accentRgb},0.06)`,
              }}
            >
              {t("landing.hiw_step2_chip_risk")}
            </span>
          </div>
          {/* Current price / Fair Value boxes — Fair Value emphasized
              with ~10% larger type, extra top breathing room and a
              single restrained left-to-right shine pass. */}
          <div className="grid grid-cols-2 gap-1.5 mt-1">
            <div className="rounded-md border border-white/10 bg-black/40 px-1.5 py-1.5 flex flex-col justify-center leading-tight">
              <div className="text-[7.5px] uppercase tracking-[0.10em] text-muted-foreground/65 font-medium">
                {t("landing.hiw_step2_current_price_label")}
              </div>
              <div className="mt-0.5 text-[13px] tabular font-medium text-foreground/95" data-testid="hiw-valuation-price-now">
                $55.04
              </div>
            </div>
            <div
              className="rounded-md border px-1.5 pt-2 pb-1.5 flex flex-col justify-center leading-tight"
              style={{
                borderColor: `rgba(${accentRgb},0.28)`,
                background: `linear-gradient(180deg, rgba(${accentRgb},0.10), rgba(${accentRgb},0.03))`,
              }}
            >
              <div className="text-[7.5px] uppercase tracking-[0.10em] text-muted-foreground/65 font-medium">
                {t("landing.hiw_step2_fair_value_label")}
              </div>
              <div
                className="mt-0.5 text-[14px] tabular font-semibold hiw-shine"
                style={{ color: accent, animationDelay: "1.6s" }}
                data-shine="$71.42"
                data-testid="hiw-valuation-price-fair"
              >
                $71.42
              </div>
            </div>
          </div>
          {/* Compact status row — Upside receives a matching shine pass
              (staggered slightly after the Fair Value price). */}
          <div className="grid grid-cols-2 gap-1.5 mt-0.5 text-[9px] tracking-[0.04em]">
            <span
              className="inline-flex items-center justify-center gap-1 rounded px-1.5 py-[3px] font-medium whitespace-nowrap"
              style={{
                color: accent,
                background: `rgba(${accentRgb},0.08)`,
              }}
              data-testid="hiw-valuation-status-upside"
            >
              <span className="opacity-70 uppercase tracking-[0.10em]">{t("landing.hiw_step2_status_upside")}</span>
              <span className="hiw-shine" style={{ color: accent, animationDelay: "2.1s" }} data-shine="+29.8%">+29.8%</span>
            </span>
            <span
              className="inline-flex items-center justify-center gap-1 rounded px-1.5 py-[3px] font-medium text-muted-foreground/85 whitespace-nowrap"
              style={{ background: "rgba(255,255,255,0.03)" }}
              data-testid="hiw-valuation-status-risk"
            >
              <span className="opacity-70 uppercase tracking-[0.10em]">{t("landing.hiw_step2_status_risk")}</span>
              <span>{t("landing.hiw_step2_risk_low")}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrendUpArrow({ accent }) {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M2 10 L9 3 M4 3 H9 V8" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ValuationCompactChart — realistic compact rendering of the real
// PayPal "Price vs Fair Value" chart.  Deterministic curated path;
// no live fetch.  Reuses the visual tokens (white current line, green
// FV line, restrained green + red zones, highlighted endpoints, mini
// gap indicator) of the real ``PriceChart`` in the Analysis Preview.
// ---------------------------------------------------------------------------
function ValuationCompactChart({ accent, accentRgb, t }) {
  // Realistic PayPal-inspired price path.  Y in a 100-unit space
  // (0 = top, 100 = bottom).  Fair Value line is a slight upward
  // slope from y=32 to y=22.  The path includes early spike, dip
  // into the middle, extended trough, and a recovery ending near
  // the NOW endpoint.  Values chosen so that the endpoint clearly
  // sits below the Fair Value line (matches +29.8% upside).
  const W = 240;
  const H = 96;
  // Coordinate space: viewBox 240 x 100. Padding for endpoint labels.
  const chartLeft = 4;
  const chartRight = 196;    // leave 44px on the right for labels
  const chartTop = 6;
  const chartBottom = 92;
  const points = [
    [0, 42], [4, 30], [8, 24], [12, 34], [16, 26], [20, 20], [24, 34],
    [28, 38], [32, 44], [36, 52], [40, 58], [44, 64], [48, 62], [52, 66],
    [56, 72], [60, 74], [64, 76], [68, 74], [72, 72], [76, 68], [80, 70],
    [84, 72], [88, 68], [92, 66], [96, 60], [100, 58], [104, 62], [108, 60],
    [112, 56], [116, 50], [120, 48], [124, 42], [128, 40], [132, 38], [136, 40],
    [140, 42], [144, 46], [148, 50], [152, 46], [156, 44], [160, 40], [164, 38],
    [168, 34], [172, 32], [176, 30], [180, 32], [184, 34], [188, 36], [192, 60], [196, 66],
  ];
  const toX = (rel) => chartLeft + rel * ((chartRight - chartLeft) / 196);
  const toY = (rel) => chartTop + rel * ((chartBottom - chartTop) / 100);
  const pricePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(" ");
  // FV line — subtle upward slope, ends higher than the price
  const fvY0 = toY(32);
  const fvY1 = toY(22);
  const fvX0 = toX(0);
  const fvX1 = toX(196);
  // Undervaluation area — between price and FV where price is
  // below FV (path fills the entire chart area under FV, above
  // the price).  We build a polygon.
  const gapAreaPath = `M${fvX0.toFixed(1)},${fvY0.toFixed(1)} L${fvX1.toFixed(1)},${fvY1.toFixed(1)} ` +
    points.slice().reverse().map(([x, y]) => `L${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(" ") + " Z";
  // Overvaluation area — thin red band above the FV line.
  const overvalY = toY(10);
  const overvalPath = `M${chartLeft},${overvalY} L${chartRight},${overvalY} L${fvX1.toFixed(1)},${fvY1.toFixed(1)} L${fvX0.toFixed(1)},${fvY0.toFixed(1)} Z`;
  const nowX = toX(196);
  const nowY = toY(66);
  const fairX = toX(196);
  const fairY = toY(22);
  return (
    <div
      className="relative px-2 pt-1.5 pb-1.5"
      data-testid="hiw-valuation-mini-graph"
      role="img"
      aria-label={t("landing.hiw_step2_graph_aria")}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H + 4}
        preserveAspectRatio="none"
        aria-hidden="true"
        className="block"
      >
        <defs>
          <linearGradient id="hiw-fv-under" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.28" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.06" />
          </linearGradient>
          <linearGradient id="hiw-fv-over" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(229,72,77,1)" stopOpacity="0.10" />
            <stop offset="100%" stopColor="rgba(229,72,77,1)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Overvaluation zone above FV line — restrained red */}
        <path d={overvalPath} fill="url(#hiw-fv-over)" />
        {/* Undervaluation zone below FV line — restrained green */}
        <path d={gapAreaPath} fill="url(#hiw-fv-under)" />
        {/* Subtle horizontal guide */}
        <line x1={chartLeft} y1={toY(50)} x2={chartRight} y2={toY(50)} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" strokeDasharray="1 2" />
        {/* Fair Value line — dashed emerald */}
        <line x1={fvX0} y1={fvY0} x2={fvX1} y2={fvY1} stroke={accent} strokeWidth="1.4" strokeDasharray="3 2" strokeLinecap="round" />
        {/* Historical price path — white with slight glow */}
        <path d={pricePath} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        {/* +29.8% gap indicator — anchored to the right, vertically
            between the FAIR VALUE and NOW endpoint labels so it
            reads as the "gap" between the two. */}
        <g transform={`translate(${(nowX + 3).toFixed(1)}, ${((fvY1 + nowY) / 2 - 4.5).toFixed(1)})`}>
          <rect x="0" y="0" width="30" height="10" rx="5" fill="rgba(0,0,0,0.72)" stroke={accent} strokeOpacity="0.7" strokeWidth="0.6" />
          <text x="15" y="7.2" textAnchor="middle" fill={accent} fontSize="6.2" fontWeight="600" fontFamily="ui-monospace, monospace">+29.8%</text>
        </g>
        {/* Endpoint dots */}
        <circle cx={nowX} cy={nowY} r="2.4" fill="#ffffff" stroke="rgba(0,0,0,0.7)" strokeWidth="0.5" />
        <circle cx={fairX} cy={fairY} r="2.4" fill={accent} stroke="rgba(0,0,0,0.7)" strokeWidth="0.5" />
        {/* Endpoint labels — text nodes inside the SVG so they
            always stay inside the chart footprint */}
        <g>
          <text x={fairX + 3} y={fvY1 - 3} fill={accent} fontSize="6" fontWeight="600" fontFamily="ui-sans-serif, system-ui" data-testid="hiw-valuation-graph-label-fair">
            {t("landing.hiw_step2_graph_fair_value_label")}
          </text>
          <text x={fairX + 3} y={fvY1 + 5} fill={accent} fontSize="7" fontFamily="ui-monospace, monospace">
            $71.42
          </text>
          <text x={nowX + 3} y={nowY + 3} fill="rgba(255,255,255,0.85)" fontSize="6" fontWeight="600" fontFamily="ui-sans-serif, system-ui" data-testid="hiw-valuation-graph-label-now">
            {t("landing.hiw_step2_graph_now_label")}
          </text>
          <text x={nowX + 3} y={nowY + 11} fill="rgba(255,255,255,0.85)" fontSize="7" fontFamily="ui-monospace, monospace">
            $55.04
          </text>
        </g>
      </svg>
    </div>
  );
}

function ThesisPreview({ accent, accentRgb }) {
  // Landing/Analysis Polish 2 — four localized categories replace
  // the anonymous placeholder bars.  Each quadrant carries a short
  // illustrative example so visitors immediately understand what
  // kind of insight Stockaily surfaces.  The fourth quadrant is
  // now "Key drivers" (was "What to Watch").
  const { t } = useTranslation();
  const quadrants = [
    {
      key: "bull",
      dot: "#3FAE89",
      labelKey: "landing.hiw_step3_bull_case",
      exampleKey: "landing.hiw_step3_bull_case_example",
    },
    {
      key: "bear",
      dot: "#E57373",
      labelKey: "landing.hiw_step3_bear_case",
      exampleKey: "landing.hiw_step3_bear_case_example",
    },
    {
      key: "risks",
      dot: "#D9A441",
      labelKey: "landing.hiw_step3_key_risks",
      exampleKey: "landing.hiw_step3_key_risks_example",
    },
    {
      key: "drivers",
      dot: "#9B7BFF",
      labelKey: "landing.hiw_step3_key_drivers",
      exampleKey: "landing.hiw_step3_key_drivers_example",
    },
  ];
  return (
    <div
      className="w-full rounded-xl border border-white/10 p-2.5 sm:p-3 grid grid-cols-2 gap-1.5 sm:gap-2"
      style={{ background: "#0A0A0C" }}
      data-testid="hiw-thesis-preview"
    >
      {quadrants.map((q, qi) => (
        <div
          key={q.key}
          className="rounded-lg border border-white/10 p-2 flex flex-col"
          style={{ background: "#0A0A0C" }}
          data-testid={`hiw-thesis-quadrant-${q.key}`}
        >
          <div className="flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: q.dot }}
              aria-hidden="true"
            />
            <span
              className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground/85 font-medium truncate"
              data-testid={`hiw-thesis-quadrant-${q.key}-label`}
            >
              {t(q.labelKey)}
            </span>
          </div>
          {/* Correction Block 2 Polish 2 revision — heading and its
              illustrative example must NOT visually merge.  We keep a
              restrained but clearly readable gap between the two
              (one design-system spacing step, ~6px).  The example text
              receives one restrained left-to-right shine, sequenced
              across the four quadrants (0.9 s stagger each). */}
          {/* Restrained but clearly perceptible sequential shine.
              Uses two visual layers:
                1. permanent base text (always readable),
                2. an aria-hidden overlay span carrying the same text
                   glyphs, absolutely positioned with pointer-events
                   none, background-clipped to the glyphs and swept
                   by a narrow white→transparent gradient.
              The overlay never replaces, hides, fades or recolors
              the base text. */}
          <div
            className="relative mt-2 text-[11px] text-foreground/85 leading-tight"
            data-testid={`hiw-thesis-quadrant-${q.key}-example`}
          >
            <span>{t(q.exampleKey)}</span>
            <span
              aria-hidden="true"
              className="hiw-shine-sweep"
              style={{ animationDelay: `${qi * 0.9}s` }}
              data-testid={`hiw-thesis-quadrant-${q.key}-shine`}
            >
              {t(q.exampleKey)}
            </span>
          </div>
          {/* Four illustrative gray lines forming a natural compact
              paragraph — normal document flow directly beneath the
              statement (no bottom anchoring, no flex-1, no
              margin-top: auto, no justify-* rule).  Widths taper
              78/65/50/35% so the block reads as a short supporting
              paragraph and leaves a small balanced amount of empty
              space beneath the last line.  Hidden from assistive
              technology. */}
          <div
            className="mt-[8px] flex flex-col gap-[7px]"
            aria-hidden="true"
            data-testid={`hiw-thesis-quadrant-${q.key}-continuation`}
          >
            <div className="h-[3px] rounded-full bg-white/[0.10] w-[78%]" />
            <div className="h-[3px] rounded-full bg-white/[0.10] w-[65%]" />
            <div className="h-[3px] rounded-full bg-white/[0.10] w-[50%]" />
            <div className="h-[3px] rounded-full bg-white/[0.10] w-[35%]" />
          </div>
        </div>
      ))}
    </div>
  );
}
